// contractors.ts — Patch 010.D.14.1. The contractor surface (list + detail
// + DBA aliases). See workers/core-os/src/routes/contractors.ts.

import { request } from './request'

export interface ContractorListRow {
  id: string
  display_name: string | null
  legal_name: string | null
  primary_contact_name: string | null
  primary_contact_email: string | null
  primary_contact_phone: string | null
  active: number
  created_at: string
  alias_count: number
  // Wave-7 D5 — per-contractor COI + W-9 compliance status for the roster
  // badge. coi_count: any COI on file; coi_latest_expiration: the newest
  // COI's expiry (null = none / unparsed); w9_status: the newest
  // non-archived W-9's lifecycle status (received / requested / superseded).
  coi_count: number
  coi_latest_expiration: string | null
  w9_status: string | null
}

export interface ContractorAlias {
  id: string
  alias_name: string
  source: string
  confidence: string
  notes: string | null
  created_at: string
}

export interface ContractorDetail {
  id: string
  display_name: string | null
  legal_name: string | null
  primary_contact_name: string | null
  primary_contact_email: string | null
  primary_contact_phone: string | null
  address: string | null
  tax_id_last4: string | null
  default_trades_json: string | null
  notes: string | null
  active: number
  created_at: string
  // Patch 010.E.3 — the 0083 ported columns.
  updated_at: string | null
  insurance_verified_date: string | null
  sms_phone: string | null
  prefers_sms_over_email: number | null
  auto_send_notifications: number | null
  license_number: string | null
  license_expiration_date: string | null
  last_worked_date: string | null
}

export interface ContractorRating {
  id: string
  contractor_id: string
  project_id: string | null
  quality_rating: number | null
  schedule_rating: number | null
  communication_rating: number | null
  cleanup_rating: number | null
  would_hire_again: string | null
  notes: string | null
  rated_by_user_id: string | null
  created_at: string
}

// Patch 010.D.9.1 — a contractor COI with its parsed fields.
export interface ContractorCoiRecord {
  id: string
  policy_number: string | null
  effective_date: string | null
  expiration_date: string | null
  carrier_name: string | null
  coverage_types: string | null
  coverage_limits_json: string | null
  named_insureds_json: string | null
  additional_insured_present: number | null
  parse_confidence: number | null
  parsed_at: string | null
  parse_error: string | null
  received_via: string
  created_at: string
}

// Patch 010.D.10.1 — a contractor W-9 record for the status indicator.
export interface ContractorW9Record {
  id: string
  status: string
  legal_name: string | null
  business_name: string | null
  tax_classification: string | null
  tin_type: string | null
  tin_last4: string | null
  requested_at: string | null
  received_at: string | null
  parsed_at: string | null
  parse_error: string | null
  created_at: string
}

export interface ContractorPatch {
  primary_contact_name?: string | null
  primary_contact_email?: string | null
  primary_contact_phone?: string | null
  sms_phone?: string | null
  license_number?: string | null
  notes?: string | null
  insurance_verified_date?: string | null
  license_expiration_date?: string | null
  last_worked_date?: string | null
  prefers_sms_over_email?: boolean
  auto_send_notifications?: boolean
}

export interface RatingInput {
  project_id?: string | null
  quality_rating?: number | null
  schedule_rating?: number | null
  communication_rating?: number | null
  cleanup_rating?: number | null
  would_hire_again?: string | null
  notes?: string | null
}

export interface ContractorListResponse {
  ok: true
  contractors: ContractorListRow[]
}

export interface ContractorDetailResponse {
  ok: true
  contractor: ContractorDetail
  aliases: ContractorAlias[]
  ratings: ContractorRating[]
  // Patch 010.D.9.1 — the contractor's COIs with parsed fields.
  coi_records: ContractorCoiRecord[]
  // Patch 010.D.10.1 — the contractor's W-9 records.
  w9_records: ContractorW9Record[]
}

// Patch 010.D.10.1 — POST /contractors/:id/request-w9 result.
export interface RequestW9Response {
  ok: true
  draft_id: string
  w9_record_created: boolean
}

// Patch 010.D.11.1 — POST /contractors/:id/request-coi result.
export interface RequestCoiResponse {
  ok: true
  draft_id: string
}

export interface AddAliasResponse {
  ok: true
  alias: ContractorAlias
  already_existed: boolean
}

export interface ContractorMergeResponse {
  ok: true
  keep_contractor_id: string
  merged_contractor_id: string
  redirected: Record<string, number>
  skipped: Record<string, number>
  aliases_absorbed: number
}

export interface ComplianceUploadResponse {
  ok: true
  coi_record_id?: string
  w9_record_id?: string
  document_id: string
}

// Patch 010.D.12 — a multipart file upload; `request` forces a JSON
// content-type, so the upload uses a raw same-origin fetch (the browser
// sets the multipart boundary; the Cloudflare Access cookie rides along).
async function uploadComplianceDoc(
  id: string,
  kind: 'coi' | 'w9',
  file: File,
): Promise<ComplianceUploadResponse> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(
    `/api/contractors/${encodeURIComponent(id)}/${kind}-upload`,
    { method: 'POST', body: fd },
  )
  const body = (await res.json().catch(() => null)) as
    | (ComplianceUploadResponse & { error?: string })
    | null
  if (!res.ok || !body) {
    throw new Error(body?.error ?? `http_${res.status}`)
  }
  return body
}

export const contractorsApi = {
  list: () => request<ContractorListResponse>('/api/contractors'),
  detail: (id: string) =>
    request<ContractorDetailResponse>(
      `/api/contractors/${encodeURIComponent(id)}`,
    ),
  addAlias: (id: string, aliasName: string) =>
    request<AddAliasResponse>(
      `/api/contractors/${encodeURIComponent(id)}/aliases`,
      { method: 'POST', body: JSON.stringify({ alias_name: aliasName }) },
    ),
  merge: (keepId: string, mergeId: string) =>
    request<ContractorMergeResponse>('/api/contractors/merge', {
      method: 'POST',
      body: JSON.stringify({
        keep_contractor_id: keepId,
        merge_contractor_id: mergeId,
      }),
    }),
  uploadCoi: (id: string, file: File) => uploadComplianceDoc(id, 'coi', file),
  uploadW9: (id: string, file: File) => uploadComplianceDoc(id, 'w9', file),
  update: (id: string, patch: ContractorPatch) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/contractors/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),
  addRating: (id: string, rating: RatingInput) =>
    request<{ ok: true; id: string }>(
      `/api/contractors/${encodeURIComponent(id)}/ratings`,
      { method: 'POST', body: JSON.stringify(rating) },
    ),
  requestW9: (id: string) =>
    request<RequestW9Response>(
      `/api/contractors/${encodeURIComponent(id)}/request-w9`,
      { method: 'POST' },
    ),
  requestCoi: (id: string) =>
    request<RequestCoiResponse>(
      `/api/contractors/${encodeURIComponent(id)}/request-coi`,
      { method: 'POST' },
    ),
}
